// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type HomeInspectionCounts = {
  total: number;
  draft: number;
  scheduled: number;
  assigned: number;
  in_progress: number;
  submitted: number;
  in_review: number;
  approved: number;
  delivered: number;
  cancelled: number;
  completed: number;
};
export const HomeInspectionCountsSchema = z.object({
  total: z.number(),
  draft: z.number(),
  scheduled: z.number(),
  assigned: z.number(),
  in_progress: z.number(),
  submitted: z.number(),
  in_review: z.number(),
  approved: z.number(),
  delivered: z.number(),
  cancelled: z.number(),
  completed: z.number(),
}).strict();

export type HomeCounts = {
  inspections: HomeInspectionCounts;
  reports: {
  total: number;
};
  subjects: {
  total: number;
};
};
export const HomeCountsSchema = z.object({
  inspections: HomeInspectionCountsSchema,
  reports: z.object({
    total: z.number(),
  }).strict(),
  subjects: z.object({
    total: z.number(),
  }).strict(),
}).strict();

export type HomeNextAction = {
  action: string;
  to: string;
  label: string;
  allowed: boolean;
  reason: string | null;
};
export const HomeNextActionSchema = z.object({
  action: z.string(),
  to: z.string(),
  label: z.string(),
  allowed: z.boolean(),
  reason: z.string().nullable(),
}).strict();

export type HomeNextWorkItem = {
  inspectionId: string;
  siteName: string | null;
  typeName: string | null;
  group: "overdue" | "today" | "upcoming" | "in_progress";
  dueState: "overdue" | "due_today" | "scheduled" | "none";
  localDay: string | null;
  localTime: string | null;
  nextAction: HomeNextAction | null;
};
export const HomeNextWorkItemSchema = z.object({
  inspectionId: z.string(),
  siteName: z.string().nullable(),
  typeName: z.string().nullable(),
  group: z.enum(["overdue", "today", "upcoming", "in_progress"] as const),
  dueState: z.enum(["overdue", "due_today", "scheduled", "none"] as const),
  localDay: z.string().nullable(),
  localTime: z.string().nullable(),
  nextAction: HomeNextActionSchema.nullable(),
}).strict();

export type HomeApprovedReport = {
  reportId: string;
  inspectionId: string | null;
  title: string | null;
  subjectName: string | null;
  version: number;
  approvedAt: string | null;
};
export const HomeApprovedReportSchema = z.object({
  reportId: z.string(),
  inspectionId: z.string().nullable(),
  title: z.string().nullable(),
  subjectName: z.string().nullable(),
  version: z.number(),
  approvedAt: z.string().nullable(),
}).strict();

export type HomeZeroState = {
  noSubjects: boolean;
  noInspections: boolean;
  noReports: boolean;
  isEmpty: boolean;
};
export const HomeZeroStateSchema = z.object({
  noSubjects: z.boolean(),
  noInspections: z.boolean(),
  noReports: z.boolean(),
  isEmpty: z.boolean(),
}).strict();

export type HomeMeta = {
  timezone: string;
  today: string;
  sourcePeriod: {
  from: string;
  to: string;
};
  generatedAt: string;
};
export const HomeMetaSchema = z.object({
  timezone: z.string(),
  today: z.string(),
  sourcePeriod: z.object({
    from: z.string(),
    to: z.string(),
  }).strict(),
  generatedAt: z.string(),
}).strict();

export type HomeResponse = {
  counts: HomeCounts;
  nextWork: Array<HomeNextWorkItem>;
  recentApprovedReports: Array<HomeApprovedReport>;
  zeroState: HomeZeroState;
  meta: HomeMeta;
};
export const HomeResponseSchema = z.object({
  counts: HomeCountsSchema,
  nextWork: z.array(HomeNextWorkItemSchema),
  recentApprovedReports: z.array(HomeApprovedReportSchema),
  zeroState: HomeZeroStateSchema,
  meta: HomeMetaSchema,
}).strict();

export type GetHomeResponse = HomeResponse;

export async function getHome(): Promise<GetHomeResponse> {
  const response = await api.get<GetHomeResponse>(`/home`);
  return response.data;
}

export const homeKeys = {
  all: ["home"] as const,
  lists: () => [...homeKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...homeKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...homeKeys.all, 'detail'] as const,
  detail: (id: string) => [...homeKeys.details(), id] as const,
};
